import { getTelegramWebApp, isTelegramSurface } from '../hooks/useTelegramContext';

/**
 * Lazy, failure-tolerant access to `@telegram-apps/sdk`.
 *
 * The package is ESM-only and throws when the Mini Apps environment is absent,
 * so it is never imported at module scope — `loadSdk()` returns `null` outside
 * Telegram and every wrapper below degrades to a no-op. That keeps the same
 * component tree working in a plain browser tab.
 */

type SdkModule = typeof import('@telegram-apps/sdk');

let modulePromise: Promise<SdkModule | null> | null = null;
let initialised = false;

export async function loadSdk(): Promise<SdkModule | null> {
  if (!isTelegramSurface()) return null;
  if (!modulePromise) {
    modulePromise = import('@telegram-apps/sdk').catch((err) => {
      console.warn('[telegram] SDK failed to load', err);
      return null;
    });
  }
  return modulePromise;
}

/** Idempotent. Mounts the pieces we drive: back button, main button, viewport. */
export async function initTelegram(): Promise<void> {
  if (initialised) return;
  const sdk = await loadSdk();
  if (!sdk) return;
  initialised = true;

  try {
    sdk.init();
  } catch (err) {
    console.warn('[telegram] init failed', err);
    return;
  }

  safe(() => {
    if (sdk.mountBackButton.isAvailable()) sdk.mountBackButton();
  });
  safe(() => {
    if (sdk.mountMainButton.isAvailable()) sdk.mountMainButton();
  });
  safe(() => {
    if (sdk.viewport.mount.isAvailable()) void sdk.viewport.mount();
  });

  // The bundled telegram-web-app.js global is still the simplest way to tell
  // the host we finished booting and to ask for the full viewport.
  const webApp = getTelegramWebApp();
  safe(() => webApp?.ready?.());
  safe(() => webApp?.expand?.());
}

function safe(fn: () => void) {
  try {
    fn();
  } catch (err) {
    console.warn('[telegram]', err);
  }
}

export interface MainButtonConfig {
  text: string;
  onClick: () => void;
  /** Shows the spinner and blocks the press. */
  loading?: boolean;
}

/**
 * Applies a MainButton config; returns a cleanup that hides it again.
 * Outside Telegram this is a no-op and the web FAB takes over instead.
 */
export async function bindMainButton(config: MainButtonConfig | null): Promise<() => void> {
  const sdk = await loadSdk();
  if (!sdk) return () => {};

  if (!config) {
    safe(() => {
      if (sdk.setMainButtonParams.isAvailable()) sdk.setMainButtonParams({ isVisible: false });
    });
    return () => {};
  }

  let off: (() => void) | undefined;
  safe(() => {
    if (sdk.setMainButtonParams.isAvailable()) {
      sdk.setMainButtonParams({
        text: config.text,
        isVisible: true,
        isEnabled: !config.loading,
        isLoaderVisible: !!config.loading,
      });
    }
  });
  safe(() => {
    if (sdk.onMainButtonClick.isAvailable()) off = sdk.onMainButtonClick(config.onClick);
  });

  return () => {
    off?.();
    safe(() => {
      if (sdk.setMainButtonParams.isAvailable()) sdk.setMainButtonParams({ isVisible: false });
    });
  };
}

/** Shows the native BackButton wired to `onBack`; returns a cleanup. */
export async function bindBackButton(onBack: (() => void) | null): Promise<() => void> {
  const sdk = await loadSdk();
  if (!sdk) return () => {};

  if (!onBack) {
    safe(() => {
      if (sdk.hideBackButton.isAvailable()) sdk.hideBackButton();
    });
    return () => {};
  }

  let off: (() => void) | undefined;
  safe(() => {
    if (sdk.showBackButton.isAvailable()) sdk.showBackButton();
  });
  safe(() => {
    if (sdk.onBackButtonClick.isAvailable()) off = sdk.onBackButtonClick(onBack);
  });

  return () => {
    off?.();
    safe(() => {
      if (sdk.hideBackButton.isAvailable()) sdk.hideBackButton();
    });
  };
}

export type HapticKind = 'light' | 'medium' | 'success' | 'error' | 'selection';

/** Haptics fire in Telegram only; PR7 calls this from write actions. */
export async function haptic(kind: HapticKind = 'light'): Promise<void> {
  const sdk = await loadSdk();
  if (!sdk) return;
  const hf = sdk.hapticFeedback;
  safe(() => {
    if (kind === 'selection') {
      if (hf.selectionChanged.isAvailable()) hf.selectionChanged();
    } else if (kind === 'success' || kind === 'error') {
      if (hf.notificationOccurred.isAvailable()) hf.notificationOccurred(kind);
    } else if (hf.impactOccurred.isAvailable()) {
      hf.impactOccurred(kind);
    }
  });
}

/**
 * Open a URL outside the app.
 *
 * `window.open` is not enough inside Telegram: the Mini App runs in a webview
 * the host controls, where a popup is either swallowed or replaces the app
 * itself with no way back. `WebApp.openLink` hands the URL to the client, which
 * opens it in its own browser and leaves the Mini App where it was.
 *
 * Uses the raw global rather than `loadSdk()` so it stays synchronous — this is
 * called straight from a click handler, and browsers block popups opened after
 * an await.
 */
export function openExternal(url: string): void {
  const webApp = getTelegramWebApp();
  if (isTelegramSurface() && webApp?.openLink) {
    safe(() => webApp.openLink?.(url));
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}
